// Web session wiring for `EphemeralClient` (`packages/ui/src/ephemeral`).

import {
  deriveEphemeralWsUrl,
  EphemeralClient,
  type EphemeralEvent,
} from '@ping/ui/src/ephemeral';
import { useSessionStore } from '@ping/ui/src/messaging/sessionStore';

import { isFeatureEnabled } from '@core/featureFlags';
import { log } from '@core/log/log';
import { getAccessToken, performRefresh } from '@core/network/interceptor';

import {
  bindEphemeralPresenceLifecycle,
  syncEphemeralPresenceState,
  unbindEphemeralPresenceLifecycle,
} from './ephemeralPresence';

let currentClient: EphemeralClient | null = null;

export function getCurrentEphemeralClient(): EphemeralClient | null {
  return currentClient;
}

export function startEphemeralSession(): void {
  if (!isFeatureEnabled('FF_EPHEMERAL_WS')) {
    return;
  }
  if (currentClient !== null) {
    return;
  }
  const status = useSessionStore.getState().status;
  if (status.kind !== 'authenticated' || status.client === null) {
    log.debug({ event: 'ephemeralSession.skip', reason: 'not_authenticated' });
    return;
  }

  const sdkClient = status.client as unknown as EphemeralSdkClient;
  const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

  const client = new EphemeralClient({
    wsUrl: deriveEphemeralWsUrl({ apiBaseUrl: apiBase }),
    getToken: () => getAccessToken(),
    getDeviceId: () => sdkClient.deviceId(),
    exporter: {
      exportConversationSecret: (conv, label, context, length) =>
        sdkClient.exportConversationSecret(conv, label, context, length),
    },
    getEpoch: async (conversationId) => {
      try {
        const conv = sdkClient.getConversation(conversationId);
        const meta = conv.meta();
        return typeof meta.epoch === 'number' ? meta.epoch : Number(meta.epoch);
      } catch {
        return 0;
      }
    },
    onReconnect: () => performRefresh().then(() => undefined),
  });

  client.onState((state) => {
    log.debug({ event: 'ephemeralSession.state', state });
    if (state === 'connected') {
      syncEphemeralPresenceState();
    }
  });

  client.onEvent((event: EphemeralEvent) => {
    log.debug({
      event: 'ephemeralSession.frame',
      type: event.type,
      convPrefix: bytesPrefix(event.conversationId),
    });
  });

  client.start();
  currentClient = client;
  bindEphemeralPresenceLifecycle();
  log.info({ event: 'ephemeralSession.started' });
}

export function stopEphemeralSession(): void {
  if (currentClient === null) return;
  void currentClient.publishPresenceToSubscribed('offline');
  currentClient.stop();
  currentClient = null;
  unbindEphemeralPresenceLifecycle();
  log.info({ event: 'ephemeralSession.stopped' });
}

interface EphemeralSdkClient {
  deviceId(): Promise<Uint8Array>;
  exportConversationSecret(
    conversationId: Uint8Array,
    label: string,
    context: Uint8Array,
    length: number,
  ): Promise<Uint8Array>;
  getConversation(conversationId: Uint8Array): {
    meta(): { epoch: number | bigint };
  };
}

function bytesPrefix(bytes: Uint8Array): string {
  return Array.from(bytes.slice(0, 4), (b) => b.toString(16).padStart(2, '0')).join('');
}
